import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { api } from "../api";
import { mandateBookingPath } from "../mandateBooking";
import PortfolioNotes from "./PortfolioNotes";
import Metrics from "./Metrics";
import Roadmap from "./Roadmap";
import Framework from "./Framework";
import Documents from "./Documents";
import Funnel from "./Funnel";

function parsePortfolioHash() {
  const h = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#/, "");
  if (h === "roadmap") return { tab: "roadmap", fwSlug: "" };
  if (h === "documents") return { tab: "documents", fwSlug: "" };
  if (h === "meetings") return { tab: "meetings", fwSlug: "" };
  if (h === "notes") return { tab: "notes", fwSlug: "" };
  if (h === "mandates") return { tab: "mandates", fwSlug: "" };
  if (h.startsWith("framework")) {
    const slug = h.startsWith("framework-") ? h.slice("framework-".length) : "";
    return { tab: "framework", fwSlug: slug };
  }
  return { tab: "metrics", fwSlug: "" };
}

function whenLabel(value, lang) {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PortfolioMeetings() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [sessions, setSessions] = useState(null);

  useEffect(() => {
    api.get("/sessions").then((r) => setSessions(r.data || [])).catch(() => setSessions([]));
  }, []);

  if (sessions === null) return <div className="tb-loading">{t("common.loading")}</div>;
  const now = Date.now();
  const upcoming = sessions.filter((s) => new Date(s.scheduled_at).getTime() > now);
  const past = sessions.filter((s) => new Date(s.scheduled_at).getTime() <= now).reverse();
  const render = (s) => {
    const href = s.type === "internal_1_1" ? `/app/team-1-1/${s.id}` : `/app/sessions/${s.id}`;
    const names = [s.advisor?.name, s.entrepreneur?.name].filter((n) => n && n !== user?.name);
    return (
      <Link key={s.id} to={href} className="tb-app-card" data-testid={`portfolio-meeting-${s.id}`}>
        <span className="tb-when">{whenLabel(s.scheduled_at, lang)}</span>
        <strong>{s.title || t("team1v1.badge.advisory")}</strong>
        {names.length > 0 && <small>{names.join(" · ")}</small>}
      </Link>
    );
  };
  if (upcoming.length === 0 && past.length === 0) {
    return <p className="tb-muted" data-testid="portfolio-meetings-empty">{t("portfolio.meetingsEmpty")}</p>;
  }
  return (
    <div data-testid="portfolio-meetings">
      <h3 className="tb-h2" style={{ marginTop: 0 }}>{t("portfolio.meetingsUpcoming")}</h3>
      {upcoming.length === 0 ? (
        <p className="tb-muted">{t("dash.noUpcoming")}</p>
      ) : (
        <div className="tb-card-grid">{upcoming.map(render)}</div>
      )}
      <h3 className="tb-h2" style={{ marginTop: "1.75rem" }}>{t("portfolio.meetingsPast")}</h3>
      {past.length === 0 ? (
        <p className="tb-muted">{t("dash.noPast")}</p>
      ) : (
        <div className="tb-card-grid">{past.map(render)}</div>
      )}
    </div>
  );
}

function PortfolioMandates() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [advisorIds, setAdvisorIds] = useState([]);

  useEffect(() => {
    api.get("/mandates").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    api.get("/sessions").then((r) => {
      const ids = (r.data || [])
        .filter((s) => s.status !== "cancelled" && s.advisor_id)
        .map((s) => s.advisor_id);
      setAdvisorIds(ids);
    }).catch(() => setAdvisorIds([]));
  }, []);

  if (items === null) return <div className="tb-loading">{t("common.loading")}</div>;
  if (items.length === 0) {
    return <p className="tb-muted" data-testid="portfolio-mandates-empty">{t("dash.noMandates")}</p>;
  }
  return (
    <div className="tb-card-grid" data-testid="portfolio-mandates">
      {items.map((m) => {
        const open = m.status === "active" || m.status === "accepted";
        const freq = t(`mandates.freq.${m.meeting_frequency || "weekly"}`);
        return (
          <div key={m.id} className="tb-app-card" data-testid={`portfolio-mandate-${m.id}`}>
            <Link to={`/app/mandates/${m.id}`} style={{ display: "flex", flexDirection: "column", gap: "0.45rem", textDecoration: "none", color: "inherit" }}>
              <strong>{m.title}</strong>
              {(m.advisor?.name || m.advisor?.company) && (
                <small>{[m.advisor.name, m.advisor.company].filter(Boolean).join(" · ")}</small>
              )}
              <small style={{ marginTop: "0.35rem" }} data-testid={`portfolio-mandate-dates-${m.id}`}>
                {t("mandates.startDate")}: {m.start_date || "—"} · {t("mandates.endDate")}: {m.end_date || "—"}
              </small>
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                <span className="tb-badge">{t(`mandates.status.${m.status}`)}</span>
                <span className="tb-badge muted">{m.meeting_duration_min || 60} min · {freq}</span>
              </div>
            </Link>
            {open && (
              <Link to={mandateBookingPath(m)} className="tb-primary-btn" style={{ marginTop: 10, display: "inline-block" }} data-testid={`portfolio-mandate-book-${m.id}`}>
                {advisorIds.includes(m.advisor_id) ? t("mandates.bookNext") : t("mandates.bookFirst")}
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Portfolio() {
  const { user } = useAuth();
  const { t } = useI18n();
  const initial = parsePortfolioHash();
  const [tab, setTab] = useState(initial.tab);
  const [fwSlug, setFwSlug] = useState(initial.fwSlug);

  useEffect(() => {
    const apply = () => {
      const next = parsePortfolioHash();
      setTab(next.tab);
      setFwSlug(next.fwSlug);
    };
    window.addEventListener("hashchange", apply);
    apply();
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;

  if (user.role !== "entrepreneur") {
    return <Funnel />;
  }

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="portfolio-heading">{t("portfolio.title")}</h1>
      <p className="tb-muted" style={{ marginBottom: "1.5rem", maxWidth: 760 }} data-testid="portfolio-subtitle">{t("portfolio.subtitleFounder")}</p>
      <div className="tb-tabs" style={{ display: "flex", gap: 6, marginBottom: "1.2rem", flexWrap: "wrap" }} data-testid="portfolio-tabs">
        <button className={`tb-chip ${tab === "metrics" ? "active" : ""}`} onClick={() => setTab("metrics")} data-testid="ptab-metrics">{t("metrics.title")}</button>
        <button className={`tb-chip ${tab === "roadmap" ? "active" : ""}`} onClick={() => setTab("roadmap")} data-testid="ptab-roadmap">{t("roadmap.title")}</button>
        <button className={`tb-chip ${tab === "framework" ? "active" : ""}`} onClick={() => setTab("framework")} data-testid="ptab-framework">{t("framework.title")}</button>
        <button className={`tb-chip ${tab === "documents" ? "active" : ""}`} onClick={() => setTab("documents")} data-testid="ptab-documents">{t("documents.title")}</button>
        <button className={`tb-chip ${tab === "meetings" ? "active" : ""}`} onClick={() => setTab("meetings")} data-testid="ptab-meetings">{t("portfolio.meetings")}</button>
        <button className={`tb-chip ${tab === "notes" ? "active" : ""}`} onClick={() => setTab("notes")} data-testid="ptab-notes">{t("portfolio.notes")}</button>
        <button className={`tb-chip ${tab === "mandates" ? "active" : ""}`} onClick={() => setTab("mandates")} data-testid="ptab-mandates">{t("nav.mandates")}</button>
      </div>
      <div className="tb-block">
        {tab === "metrics" && <Metrics hideHeader snapshotInline />}
        {tab === "roadmap" && <Roadmap hideHeader snapshotInline />}
        {tab === "framework" && <Framework hideHeader focusSlug={fwSlug} />}
        {tab === "documents" && <Documents hideHeader />}
        {tab === "meetings" && <PortfolioMeetings />}
        {tab === "notes" && <PortfolioNotes />}
        {tab === "mandates" && <PortfolioMandates />}
      </div>
    </div>
  );
}
